import React, { useContext, useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Flex,
  Checkbox,
  Spin,
  Alert,
  Typography,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  CloseOutlined,
  LockOutlined,
  UserOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { TResponseModel, TUser } from "../../types/config";
import EncryptUtils from "../../utils/encrypt";
import useFetch from "../../hooks/useFetch";
import {
  ApiSignin,
  GetMenuHierarchyList,
  ApiSigninWithToken,
} from "../../utils/api.constant";
import { eAuthStatus, eResultCode } from "../../utils/enum";
import useToast from "../../hooks/useToast";
import AuthUtil from "../../utils/auth";
import { ToastOpen, ToastType } from "../../state/toast/slice";
import Utils from "../../utils";
import styles from "./auth.module.css";
import { getToken, onMessage } from "firebase/messaging";
import { messaging } from "../../firebase/firebase-config";
const { Text } = Typography;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { post } = useFetch();
  const { showToast, onShowToast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const vapidKey = process.env.VAPID_KEY;

  const setupNotification = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        const fcmToken = await getToken(messaging, { vapidKey });
        if (fcmToken) {
          const payload = {
            data: {
              action: eAuthStatus.LOGIN,
              fcmToken: fcmToken,
            },
          };
          await post("/registerFCMToken", payload);
        } else {
          console.warn("No token received");
        }
      }
    } catch (err) {
      console.error("Notification setup error:", err);
    }
  };

  // Foreground messages
  onMessage(messaging, (payload) => {
    const { title, body, icon } = payload.notification || {};
    new Notification(title ?? "Notification", {
      body,
      icon,
    });
  });

  const onFinish = async (values: TUser) => {
    try {
      setIsLoading(true);
      const encryptedPassword = EncryptUtils.encrypt(values.password as string);
      // Prepare payload for API request
      const payload = {
        data: {
          emailId: values.emailId,
          password: encryptedPassword,
        },
      };

      // Make API request for sign-in
      const response: TResponseModel = await post(ApiSignin, payload);

      // Handle API response
      const { dataResponse, data } = response;
      const { returnCode, description } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        AuthUtil.setToken(data as string);
        // AuthUtil.activeLoggedUserService();
        // Display success toast
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        setupNotification();
        setIsLoading(false);
        // Redirect to dashboard after a brief delay
        setTimeout(async () => {
          const menus = await getMenuHierarchyList();
          Utils.redirectUrl(menus?.[0]?.entityUrl);
        }, 700);
      } else {
        // Display error toast
        setIsLoading(false);
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch menu hierarchy list from the server
  const getMenuHierarchyList = async () => {
    try {
      const requestPayload = {
        data: {
          renderMenuRoleWise: true,
        },
      };
      const response = await post(GetMenuHierarchyList, requestPayload);
      const { data, dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        return data;
      } else {
        console.error(`Error while fetching menu hierarchy: ${description}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onClickRegister = () => {
    navigate("/register");
  };
  const onClickForgotPassword = () => {
    navigate("/forgot-password");
  };

  // Show login form
  return (
    <>
      <div className={styles.parentWrapper}>
        <Form
          name='login'
          initialValues={{ remember: true }}
          style={{}}
          onFinish={onFinish}
          size='middle'
        >
          <div className={styles.formHeader}>
            <div className={styles.logoContainer}>
              <img
                src='/webpm-loader.gif'
                alt='loader'
                className={styles.logo}
              />
            </div>
            <h3 className={styles.title}>Welcome Back</h3>
            <p className={styles.subtitle}>
              Sign in to continue to your dashboard
            </p>
          </div>
          <Form.Item
            name='emailId'
            rules={[{ required: true, message: "Please input your Email Id!" }]}
            className={styles.formItem}
          >
            <Input
              prefix={<UserOutlined className={styles.inputIcon} />}
              placeholder='Email Address'
              size='large'
              className={styles.input}
            />
          </Form.Item>
          <Form.Item
            name='password'
            rules={[{ required: true, message: "Please input your Password!" }]}
            className={styles.formItem}
          >
            <Input.Password
              prefix={<LockOutlined className={styles.inputIcon} />}
              type='password'
              placeholder='Password'
              size='large'
              className={styles.input}
            />
          </Form.Item>
          <Form.Item className={styles.forgotPasswordItem}>
            <Flex
              justify='flex-end'
              align='center'
            >
              <Button
                type='link'
                onClick={onClickForgotPassword}
                className={styles.forgotPasswordLink}
              >
                Forgot Password?
              </Button>
            </Flex>
          </Form.Item>

          <Form.Item className={styles.submitButtonItem}>
            <Button
              block
              type='primary'
              htmlType='submit'
              loading={isLoading}
              size='large'
              className={styles.submitButton}
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </>
  );
};

export default Login;
